import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Phone, CheckCircle, Clock, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

// Static procedure data
const proceduresData = {
  "cochlear-implant-surgery": {
    title: "Cochlear Implant Surgery",
    category: "cochlear_implant",
    overview: "Cochlear implant surgery is a life-changing procedure that provides hearing to people with severe to profound hearing loss who don't benefit from hearing aids.",
    benefits: [
      "Restores functional hearing in severe to profound hearing loss",
      "Enables speech and language development in children",
      "Improves quality of life and social participation",
      "Long-lasting results with proper care",
    ],
    processSteps: [
      { title: "Pre-operative Assessment", description: "Comprehensive hearing evaluation, imaging, and medical clearance." },
      { title: "Surgery Day", description: "The procedure takes 2-3 hours under general anesthesia." },
      { title: "Recovery", description: "Hospital stay of 1-2 days, wound healing over 2-4 weeks." },
      { title: "Activation", description: "Device is switched on 2-4 weeks after surgery - your first hearing experience." },
      { title: "Mapping & Rehabilitation", description: "Regular programming sessions to optimize hearing outcomes." },
    ],
    recovery: "Most patients go home the next day. Full recovery takes 2-4 weeks. Activation of the implant happens 2-4 weeks post-surgery, followed by ongoing auditory rehabilitation.",
    risks: [
      "Infection (rare with proper care)",
      "Temporary dizziness",
      "Facial nerve weakness (very rare)",
      "Device failure (can be revised)",
    ],
  },
  "fess": {
    title: "FESS (Functional Endoscopic Sinus Surgery)",
    category: "nose_sinus",
    overview: "FESS is a minimally invasive procedure using an endoscope to treat chronic sinusitis, nasal polyps, and other sinus conditions.",
    benefits: [
      "Minimally invasive with no external incisions",
      "Faster recovery compared to traditional surgery",
      "Preserves natural sinus anatomy",
      "High success rate for chronic sinusitis",
    ],
    processSteps: [
      { title: "Pre-operative CT Scan", description: "Detailed imaging to plan the surgical approach." },
      { title: "Surgery", description: "Performed through the nostrils using an endoscope, typically 1-2 hours." },
      { title: "Same-Day Discharge", description: "Most patients go home the same day." },
      { title: "Post-operative Care", description: "Nasal irrigation and follow-up visits for optimal healing." },
    ],
    recovery: "Recovery takes 1-2 weeks. Nasal congestion and mild discomfort are common initially. Regular nasal irrigation is important for healing.",
    risks: [
      "Bleeding",
      "Infection",
      "CSF leak (rare)",
      "Recurrence of polyps",
    ],
  },
};

export default function ProcedurePage() {
  const { slug } = useParams();
  const procedure = proceduresData[slug];

  if (!procedure) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Procedure not found</h1>
          <Link to="/services">
            <Button>View All Services</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`procedure-page-${slug}`}>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container-width">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-sky-600">
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-12 md:py-16 border-b">
        <div className="container-width">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {procedure.title}
            </h1>
            <p className="text-lg text-slate-600">
              {procedure.overview}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Benefits */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Benefits</h2>
                <ul className="space-y-3">
                  {procedure.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Process */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-6">The Process</h2>
                <div className="space-y-6">
                  {procedure.processSteps.map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-8 h-8 rounded-full bg-sky-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{step.title}</h3>
                        <p className="text-slate-600 text-sm mt-1">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recovery */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-sky-600" />
                  Recovery
                </h2>
                <p className="text-slate-600">{procedure.recovery}</p>
              </div>

              {/* Risks */}
              <div className="bg-amber-50 rounded-xl p-8">
                <h2 className="text-xl font-bold text-amber-900 mb-4 flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  Potential Risks
                </h2>
                <p className="text-amber-800 mb-4 text-sm">
                  Like all surgical procedures, there are some risks, though complications are rare with experienced surgeons.
                </p>
                <ul className="space-y-2">
                  {procedure.risks.map((risk, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-amber-600 mt-2 flex-shrink-0" />
                      <span className="text-amber-800">{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* CTA Card */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-4">Ready to Discuss?</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Consult with Dr. Sanjay Sachdeva to determine if this procedure is right for you.
                  </p>
                  <div className="space-y-3">
                    <Link to="/book-appointment">
                      <Button className="w-full bg-amber-600 hover:bg-amber-700">
                        <Calendar className="w-4 h-4 mr-2" />
                        Book Consultation
                      </Button>
                    </Link>
                    <a href="tel:+919910317286">
                      <Button variant="outline" className="w-full">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Experience Card */}
                <div className="bg-sky-50 rounded-xl p-6">
                  <h3 className="font-bold text-slate-900 mb-2">Why Choose Dr. Sachdeva?</h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-sky-600" />
                      37+ years of surgical experience
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-sky-600" />
                      International training
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-sky-600" />
                      State-of-the-art facilities
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-12">
        <div className="container-width text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Have Questions About {procedure.title}?</h2>
          <p className="text-slate-300 mb-6">Book a consultation to discuss your options.</p>
          <Link to="/book-appointment">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700">
              Book Appointment
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
